// src/features/players/components/AdvertisingTab.tsx

import { useMemo } from 'react';
import {
  Card, Group, Text, Button, Stack, Box, Switch, Select, NumberInput, TextInput, Divider,
} from '@mantine/core';
import { useForm, useController, type Control } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { notifications } from '@mantine/notifications';
import { adFormSchema } from '../schemas/PlayerForm.schema';
import { useUpdatePlayer } from '../usePlayerMutations';
import { useVideoPlaylists } from '../useVideoPlaylists';
import { PlayerUtils } from '../PlayerUtils';
import {
  PLAYER_CONSTRAINTS,
  AD_SOURCE_TYPES,
  AD_SOURCE_TYPE_LABELS,
  AD_MIDROLL_INJECT_TYPES,
  AD_MIDROLL_INJECT_TYPE_LABELS,
} from '@/lib/constants/player';
import type { PlayerViewData, PlayerAdFormData } from '@/lib/types/Player';

interface AdvertisingTabProps {
  player: PlayerViewData;
}

export function AdvertisingTab({ player }: AdvertisingTabProps) {
  const playerId = player.core.playerId;
  const updateMutation = useUpdatePlayer(playerId);

  const { control, handleSubmit, formState: { isDirty, errors }, reset, watch } = useForm<PlayerAdFormData>({
    resolver: zodResolver(adFormSchema),
    defaultValues: PlayerUtils.transformToAdForm(player),
  });

  const adSourceType = watch('adSourceType');
  const adMidrollInjectType = watch('adMidrollInjectType');
  const adsEnabled = adSourceType !== 'none';

  const onSubmit = async (data: PlayerAdFormData) => {
    try {
      const req = PlayerUtils.transformAdFormToUpdate(data, player);
      await updateMutation.mutateAsync(req);
      reset(data);
      notifications.show({ title: 'Saved', message: 'Advertising settings updated', color: 'green' });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to save changes';
      notifications.show({ title: 'Error', message, color: 'red' });
    }
  };

  const onInvalid = (fieldErrors: typeof errors) => {
    const firstError = Object.values(fieldErrors).find(e => e?.message);
    if (firstError?.message) {
      notifications.show({ title: 'Validation Error', message: firstError.message, color: 'orange' });
    }
  };

  const isPending = updateMutation.isPending;

  return (
    <form onSubmit={handleSubmit(onSubmit, onInvalid)}>
      <Stack gap="xl" maw={800}>
        <Box
          pos="sticky"
          top="calc(var(--app-shell-header-height, 60px) + var(--mantine-spacing-md))"
          bg="white"
          py="sm"
          style={{ zIndex: 10, borderBottom: '1px solid var(--mantine-color-gray-2)' }}
        >
          <Group justify="space-between">
            <Text fw={600} size="lg">Advertising</Text>
            <Group gap="sm">
              {isDirty && (
                <Button variant="default" onClick={() => reset()} disabled={isPending}>
                  Discard
                </Button>
              )}
              <Button type="submit" disabled={!isDirty} loading={isPending}>
                Save Changes
              </Button>
            </Group>
          </Group>
        </Box>

        <Card shadow="sm" padding="lg" withBorder>
          <Stack gap="md">
            {/* Ad Source */}
            <Text fw={600} size="sm" c="dimmed" tt="uppercase">Ad Source</Text>
            <AdSourceTypeField control={control} />

            {adSourceType === 'vast' && <VastUrlField control={control} />}
            {adSourceType === 'playlist' && <AdPlaylistField control={control} />}

            {adsEnabled && (
              <>
                <Divider label="Pre-Roll" labelPosition="left" />
                <PrerollField control={control} />

                <Divider label="Mid-Roll" labelPosition="left" />
                <MidrollInjectTypeField control={control} />
                {adMidrollInjectType === 'auto' && <AutoBreakIntervalField control={control} />}
                <BreakMaxDurationField control={control} />
                <NoBreakIntervalField control={control} />

                <Divider label="Bumper" labelPosition="left" />
                <BumperPlaylistField control={control} />
              </>
            )}
          </Stack>
        </Card>
      </Stack>
    </form>
  );
}

// --- Field components ---

function AdSourceTypeField({ control }: { control: Control<PlayerAdFormData> }) {
  const { field } = useController({ control, name: 'adSourceType' });
  return (
    <Select
      label="Source Type"
      value={field.value}
      onChange={(val) => val && field.onChange(val)}
      data={AD_SOURCE_TYPES.map(t => ({ value: t, label: AD_SOURCE_TYPE_LABELS[t] }))}
      w={300}
    />
  );
}

function VastUrlField({ control }: { control: Control<PlayerAdFormData> }) {
  const { field, fieldState } = useController({ control, name: 'adSourceVasturl1' });
  return (
    <TextInput
      label="VAST Tag URL"
      required
      placeholder="https://ad-server.example.com/vast"
      value={field.value}
      onChange={field.onChange}
      onBlur={field.onBlur}
      error={fieldState.error?.message}
    />
  );
}

function AdPlaylistField({ control }: { control: Control<PlayerAdFormData> }) {
  const { options, isLoading } = useVideoPlaylists();
  const { field, fieldState } = useController({ control, name: 'adSourceListId' });

  const selectData = useMemo(() => {
    if (!options) return [];
    if (field.value != null && !options.some(o => o.value === String(field.value))) {
      return [{ value: String(field.value), label: `Playlist #${field.value} (not found)` }, ...options];
    }
    return options;
  }, [options, field.value]);

  return (
    <Select
      label="Ad Playlist"
      required
      placeholder="Select a video playlist"
      data={selectData}
      value={field.value != null ? String(field.value) : null}
      onChange={(val) => field.onChange(val ? Number(val) : null)}
      onBlur={field.onBlur}
      error={fieldState.error?.message}
      disabled={isLoading}
      searchable
      clearable
      w={400}
      nothingFoundMessage="No video playlists found"
    />
  );
}

function PrerollField({ control }: { control: Control<PlayerAdFormData> }) {
  const { field } = useController({ control, name: 'adPreroll' });
  return (
    <Switch
      label="Enable Pre-Roll"
      checked={field.value}
      onChange={(e) => field.onChange(e.currentTarget.checked)}
    />
  );
}

function MidrollInjectTypeField({ control }: { control: Control<PlayerAdFormData> }) {
  const { field } = useController({ control, name: 'adMidrollInjectType' });
  return (
    <Select
      label="Mid-Roll Injection"
      value={field.value}
      onChange={(val) => val && field.onChange(val)}
      data={AD_MIDROLL_INJECT_TYPES.map(t => ({ value: t, label: AD_MIDROLL_INJECT_TYPE_LABELS[t] }))}
      w={250}
    />
  );
}

function AutoBreakIntervalField({ control }: { control: Control<PlayerAdFormData> }) {
  const { field, fieldState } = useController({ control, name: 'adMidrollAutoBreakInterval' });
  return (
    <NumberInput
      label="Auto Break Interval (seconds)"
      value={field.value}
      onChange={(val) => field.onChange(typeof val === 'number' ? val : 0)}
      onBlur={field.onBlur}
      error={fieldState.error?.message}
      min={1}
      w={250}
    />
  );
}

function BreakMaxDurationField({ control }: { control: Control<PlayerAdFormData> }) {
  const { field, fieldState } = useController({ control, name: 'adBreakMaxDuration' });
  return (
    <NumberInput
      label="Break Max Duration (seconds)"
      description={`${PLAYER_CONSTRAINTS.AD_BREAK_MAX_DURATION_MIN}-${PLAYER_CONSTRAINTS.AD_BREAK_MAX_DURATION_MAX} seconds`}
      value={field.value}
      onChange={(val) => field.onChange(typeof val === 'number' ? val : 0)}
      onBlur={field.onBlur}
      error={fieldState.error?.message}
      min={PLAYER_CONSTRAINTS.AD_BREAK_MAX_DURATION_MIN}
      max={PLAYER_CONSTRAINTS.AD_BREAK_MAX_DURATION_MAX}
      w={250}
    />
  );
}

function NoBreakIntervalField({ control }: { control: Control<PlayerAdFormData> }) {
  const { field, fieldState } = useController({ control, name: 'adNobreakInterval' });
  return (
    <NumberInput
      label="No-Break Interval (seconds)"
      description="Minimum time between ad breaks"
      value={field.value}
      onChange={(val) => field.onChange(typeof val === 'number' ? val : 0)}
      onBlur={field.onBlur}
      error={fieldState.error?.message}
      min={0}
      w={250}
    />
  );
}

function BumperPlaylistField({ control }: { control: Control<PlayerAdFormData> }) {
  const { options, isLoading } = useVideoPlaylists();
  const { field } = useController({ control, name: 'adBumperListId' });

  const selectData = useMemo(() => {
    if (!options) return [];
    if (field.value != null && !options.some(o => o.value === String(field.value))) {
      return [{ value: String(field.value), label: `Playlist #${field.value} (not found)` }, ...options];
    }
    return options;
  }, [options, field.value]);

  return (
    <Select
      label="Bumper Playlist"
      description="Optional — plays a short bumper before the main content"
      placeholder="None"
      data={selectData}
      value={field.value != null ? String(field.value) : null}
      onChange={(val) => field.onChange(val ? Number(val) : null)}
      disabled={isLoading}
      searchable
      clearable
      w={400}
      nothingFoundMessage="No video playlists found"
    />
  );
}
